import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  ScrollArea,
  DeleteConfirmDialog,
} from 'design-system'
import * as tableOps from 'store/tableOpsQueue'
import { flushTableOps } from 'services/TableSyncService'
import { flushOfflineBootstrapPendingCounts } from 'services/offlineBootstrap'
import { checkConnectivity } from 'utils/connectivityCheck'
import { erpToast } from 'design-system/components/erp-toast'
import { RotateCcw, Trash2 } from 'lucide-react'

function conflictHint(op: tableOps.FailedTableOp, t: (key: string) => string): string | null {
  if (op.op.status !== 'conflict') return null
  if (op.op.op === 'draft') {
    return t('common.failedTableOps.conflictDraft')
  }
  return t('common.failedTableOps.conflictGeneric')
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCountsMayHaveChanged: () => Promise<void>
}

export function FailedTableOpsDialog({ open, onOpenChange, onCountsMayHaveChanged }: Props) {
  const { t } = useTranslation()
  const [items, setItems] = useState<tableOps.FailedTableOp[]>([])
  const [dismissId, setDismissId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const opLabel = (op: string) => {
    const key = `common.failedTableOps.op.${op}`
    const translated = t(key)
    return translated === key ? op : translated
  }

  const reload = useCallback(async () => {
    setItems(await tableOps.getFailedTableOps())
  }, [])

  useEffect(() => {
    if (!open) return
    void reload()
  }, [open, reload])

  useEffect(() => {
    const onEvt = () => { void reload() }
    window.addEventListener('pos:table-ops-updated', onEvt)
    return () => window.removeEventListener('pos:table-ops-updated', onEvt)
  }, [reload])

  const refreshBootstrap = async () => {
    await flushOfflineBootstrapPendingCounts()
    await onCountsMayHaveChanged()
  }

  const handleRetry = async (opId: string) => {
    if (!(await checkConnectivity())) {
      erpToast.warning(t('common.errors.noInternet'))
      return
    }
    setBusyId(opId)
    try {
      const ok = await tableOps.moveFailedTableOpToPending(opId)
      if (!ok) {
        erpToast.error(t('common.failedTableOps.retryQueueFailed'))
        return
      }
      const result = await flushTableOps()
      await reload()
      await refreshBootstrap()
      if (result.errors.length) {
        erpToast.warning(result.errors.join(' · '))
      } else {
        erpToast.success(t('common.failedTableOps.syncSuccess'))
      }
    } finally {
      setBusyId(null)
    }
  }

  const handleDismiss = async (opId: string) => {
    await tableOps.removeFailedTableOpById(opId)
    setDismissId(null)
    await reload()
    await refreshBootstrap()
    erpToast.success(t('common.failedTableOps.dismissSuccess'))
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle>{t('common.failedTableOps.title')}</DialogTitle>
            <DialogDescription>
              {t('common.failedTableOps.description')}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[min(60vh,420px)] pe-2">
            {items.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('common.failedTableOps.empty')}</p>
            ) : (
              <ul className="space-y-3">
                {items.map((entry) => (
                  <li
                    key={entry.op.id}
                    className="rounded-xl border border-border bg-card p-3 text-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold">{opLabel(entry.op.op)}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{entry.reason}</p>
                        {conflictHint(entry, t) ? (
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{conflictHint(entry, t)}</p>
                        ) : null}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {new Date(entry.failedAt).toLocaleString('ar-EG')}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={busyId === entry.op.id}
                          onClick={() => void handleRetry(entry.op.id)}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDismissId(entry.op.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={dismissId != null}
        onOpenChange={(o) => { if (!o) setDismissId(null) }}
        onConfirm={() => dismissId && void handleDismiss(dismissId)}
        title={t('common.failedTableOps.confirmDismissTitle')}
        description={t('common.failedTableOps.confirmDismissDescription')}
        confirmText={t('common.failedTableOps.confirmDismissConfirm')}
        cancelText={t('common.cancel')}
      />
    </>
  )
}

export default FailedTableOpsDialog
